import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import * as api from '../../../api';
import { Asset } from '../../../api';
import { getAssetsFilter } from '../../app/selectors';
import { assetImageUrl, MagicRef, refAssetId } from '../model';
import { TM } from '../tokens';
import { CloseIcon, SearchIcon } from '../icons';
import { AssetThumb } from './AssetThumb';

// Top results returned per search. The picker shows the most relevant matches
// rather than paginating — enough to find a reference without a "load more".
const SEARCH_LIMIT = 100;
const SEARCH_DEBOUNCE_MS = 300;

// Sub-modal to pick an additional reference asset. With an empty box it shows
// the assets already loaded in the current Asset Lab view (instant, no request);
// once the user types it searches the whole project — across every category —
// so a character can be paired with a background, a prop from another folder, etc.
export const AssetPicker: React.FC<{
  allAssets: Asset[];
  alreadyRefs: MagicRef[];
  onPick: (asset: Asset) => void;
  onClose: () => void;
}> = ({ allAssets, alreadyRefs, onPick, onClose }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Asset[]>([]);
  const [searching, setSearching] = useState(false);
  const [failed, setFailed] = useState(false);

  // Scope the search to the current project(s) but drop category / tag /
  // extension so results span the whole project, not the active category.
  const projectId = useSelector(getAssetsFilter).project_id;

  // Guards against stale responses: only the latest in-flight request may write
  // results (debounce + fast typing can leave older requests resolving later).
  const reqIdRef = useRef(0);

  const query = q.trim();
  const searched = query.length > 0;

  useEffect(() => {
    if (!searched) {
      reqIdRef.current += 1; // cancel any in-flight search
      setResults([]);
      setSearching(false);
      setFailed(false);
      return;
    }
    setSearching(true);
    setFailed(false);
    const reqId = (reqIdRef.current += 1);
    const timer = setTimeout(async () => {
      const res = await api.loadAssets({
        project_id: projectId,
        search: query,
        limit: SEARCH_LIMIT,
      });
      if (reqIdRef.current !== reqId) return; // superseded by a newer query
      if (res.success) {
        setResults(res.data.response.assets);
      } else {
        setResults([]);
        setFailed(true);
      }
      setSearching(false);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, searched, projectId]);

  const filtered = useMemo(() => {
    const refIds = new Set(alreadyRefs.map(refAssetId).filter((id): id is number => id !== null));
    return (searched ? results : allAssets).filter((a) => !refIds.has(a.id));
  }, [searched, results, allAssets, alreadyRefs]);

  let emptyMessage: string | null = null;
  if (filtered.length === 0) {
    if (searching) {
      emptyMessage = 'Searching the whole project…';
    } else if (failed) {
      emptyMessage = 'Search failed — please try again.';
    } else if (searched) {
      emptyMessage = `No assets in this project match “${query}”.`;
    } else {
      emptyMessage = 'No assets loaded yet — type to search the whole project.';
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.35)',
        zIndex: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 720,
          height: 540,
          background: '#fff',
          borderRadius: 10,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
      >
        <div
          style={{
            padding: 14,
            borderBottom: `1px solid ${TM.border}`,
            display: 'flex',
            gap: 10,
            alignItems: 'center',
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 700, color: TM.text }}>
            Pick reference from Asset Lab
          </div>
          <div style={{ flex: 1 }} />
          <div className="al-magic-search" style={{ width: 320 }}>
            <span style={{ color: TM.textFaint, display: 'inline-flex' }}>
              <SearchIcon size={14} />
            </span>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search the whole project…"
              autoFocus
            />
            {q && (
              <button type="button" onClick={() => setQ('')} aria-label="Clear search">
                <CloseIcon size={12} />
              </button>
            )}
          </div>
        </div>
        <div
          style={{
            flex: 1,
            padding: 14,
            overflowY: 'auto',
            overflowX: 'hidden',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gap: 10,
            alignContent: 'start',
          }}
        >
          {emptyMessage ? (
            <div
              style={{
                gridColumn: '1 / -1',
                textAlign: 'center',
                color: TM.textFaint,
                fontSize: 13,
                paddingTop: 40,
              }}
            >
              {emptyMessage}
            </div>
          ) : (
            filtered.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => onPick(a)}
                title={a.name}
                style={{
                  minWidth: 0,
                  padding: 6,
                  borderRadius: 8,
                  border: `1px solid ${TM.border}`,
                  background: '#fff',
                  cursor: 'pointer',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                <AssetThumb src={assetImageUrl(a)} name={a.name} fill />
                <div
                  style={{
                    fontSize: 11,
                    lineHeight: 1.3,
                    color: TM.text,
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {a.name}
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
